import { AnyAction } from "@reduxjs/toolkit";
import chunk from "lodash/chunk";

import firestore from "src/services/firestore";

// Inner imports
import { COLLECTION_IDS } from "./constants";

// Import the FirestoreDocumentSnapshot type and FieldPath
import {
  FieldPath,
  FirestoreQueryDocumentSnapshot,
} from "src/services/firebaseTypes";

export const onStateIdle = (
  state: Store.RootState[keyof Store.RootState],
  action: AnyAction,
): void => setState(state, action, "idle");

export const onStatePending = (
  state: Store.RootState[keyof Store.RootState],
  action: AnyAction,
): void => {
  // Check if the action's meta has skipGlobalLoader
  const skipGlobalLoader = action.meta?.arg?.skipGlobalLoader ?? false;
  setState(state, action, "loading", skipGlobalLoader);
};

export const onStateRejected = (
  state: Store.RootState[keyof Store.RootState],
  action: AnyAction,
): void => setState(state, action, "failed");

export const onStateFulfilled = (
  state: Store.RootState[keyof Store.RootState],
  action: AnyAction,
): void => setState(state, action, "succeeded");

export const getTimestamps = (): Record<keyof Timestamps, ISODateString> => {
  const dateNow = new Date().toISOString();

  return {
    createdAt: dateNow,
    updatedAt: dateNow,
  };
};

export const getFirestoreEntitiesById = async (
  ids: DocId[],
  collection: ValueOf<typeof COLLECTION_IDS>,
): Promise<FirestoreQueryDocumentSnapshot[]> => {
  const chunks = chunk(ids, 10);

  const queries = new Set<Promise<any>>();

  for (const chunk of chunks) {
    const query = firestore()
      .collection(collection)
      .where(FieldPath.documentId(), "in", chunk)
      .get();

    queries.add(query);
  }

  const result = await Promise.all([...queries]);

  const documents = new Set<FirestoreQueryDocumentSnapshot>();

  for (const { docs } of result) {
    for (const doc of docs) {
      documents.add(doc);
    }
  }

  return [...documents].sort((a, b) => {
    const [aIdIndex, bIdIndex] = [
      ids.findIndex((id) => id === a.id),
      ids.findIndex((id) => id === b.id),
    ];

    return aIdIndex - bIdIndex;
  });
};

export const generateDocId = (): string =>
  firestore().collection(COLLECTION_IDS.app).doc().id;

function setState(
  state: Store.RootState[keyof Store.RootState],
  action: AnyAction,
  status: LoadingStatus,
  skipGlobalLoader: boolean = false,
): void {
  if (!state) return;

  // Only set status if we're not skipping the global loader
  if ("status" in state) {
    // If skipGlobalLoader is true and the status is "loading",
    // don't update the status. Otherwise, update it.
    if (!(skipGlobalLoader && status === "loading")) {
      state.status = status;
    }
  }

  if ("error" in state) state.error = action.error || null;
}
